import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, FileText, Loader2, MessageSquare, Search, User, X } from "lucide-react";

import {
  useAcademyRosterMembers,
  useDecideRosterMember,
  useMyAthletes,
} from "../../hooks/queries/useAcademies";
import defaultAvatar from "../../utils/profile.png";

const normalize = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const Avatar = ({ src, name }) => (
  <img
    src={src || defaultAvatar}
    alt=""
    aria-hidden="true"
    onError={(e) => {
      e.currentTarget.src = defaultAvatar;
    }}
    className="h-11 w-11 shrink-0 rounded-full border border-gray-100 object-cover"
    title={name}
  />
);

const athleteLine = (row) =>
  [row.position && row.position !== "Sin posición" ? row.position : null, row.age > 0 ? `${row.age} años` : null, row.category]
    .filter(Boolean)
    .join(" · ");

/**
 * Who started a pending row. The backend sends `requested_by` ("athlete" |
 * "academy"); `requested_by_athlete` is the older boolean, kept as a fallback.
 */
const isAthleteRequest = (row) =>
  row.requested_by ? row.requested_by === "athlete" : Boolean(row.requested_by_athlete);

/**
 * Pending rows for a verified coach, in two groups (DEV-51):
 *   - athletes who asked to join: the coach confirms or rejects them;
 *   - athletes the academy invited: only the athlete can accept, so the coach
 *     can only withdraw the invitation.
 */
const PendingRequests = ({ academy, pending }) => {
  const decide = useDecideRosterMember();
  const [bulk, setBulk] = useState(null); // { done, total } while confirming all
  const [bulkError, setBulkError] = useState(null);

  const requests = pending.filter(isAthleteRequest);
  const invitations = pending.filter((row) => !isAthleteRequest(row));

  const confirmAll = async () => {
    setBulkError(null);
    setBulk({ done: 0, total: requests.length });
    let done = 0;
    for (const row of requests) {
      try {
        await decide.mutateAsync({ id: row.id, decision: "confirm" });
      } catch (error) {
        setBulkError(error.message);
        break;
      }
      done += 1;
      setBulk({ done, total: requests.length });
    }
    setBulk(null);
  };

  const busyId = decide.isPending ? decide.variables?.id : null;

  return (
    <section className="rounded-xl border border-amber-200 bg-white shadow-sm" aria-labelledby="pending-heading">
      <div className="flex flex-col gap-3 border-b border-amber-100 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 id="pending-heading" className="text-base font-bold text-gray-900">
            Solicitudes de ingreso · {requests.length}
          </h2>
          <p className="text-xs text-gray-500">
            Deportistas que pidieron unirse a {academy.name}. Al confirmarlos verás su perfil y sus reportes.
          </p>
        </div>
        {requests.length > 1 && (
          <button
            type="button"
            onClick={confirmAll}
            disabled={Boolean(bulk) || decide.isPending}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-blue-900 px-4 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
          >
            {bulk ? (
              <>
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                Confirmando {bulk.done}/{bulk.total}
              </>
            ) : (
              `Confirmar todas (${requests.length})`
            )}
          </button>
        )}
      </div>
      {(bulkError || (decide.isError && !bulk)) && (
        <p role="alert" className="px-4 pt-3 text-xs text-red-600">
          {bulkError || decide.error.message}
        </p>
      )}
      {requests.length === 0 && (
        <p className="px-4 py-3 text-xs text-gray-500">No hay solicitudes de deportistas por revisar.</p>
      )}
      <ul className="max-h-[420px] divide-y divide-gray-100 overflow-y-auto">
        {requests.map((row) => (
          <li key={row.id} className="flex items-center gap-3 px-4 py-3">
            <Avatar src={row.avatar_url} name={row.full_name} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900">{row.full_name}</p>
              <p className="truncate text-xs text-gray-500">
                Pidió unirse
                {athleteLine(row) ? ` · ${athleteLine(row)}` : ""}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => decide.mutate({ id: row.id, decision: "remove" })}
                disabled={Boolean(bulk) || busyId === row.id}
                aria-label={`Rechazar a ${row.full_name}`}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-red-600 disabled:opacity-60"
              >
                <X size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => decide.mutate({ id: row.id, decision: "confirm" })}
                disabled={Boolean(bulk) || busyId === row.id}
                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-blue-900 px-3 text-xs font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
              >
                {busyId === row.id ? (
                  <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                ) : (
                  <Check size={14} aria-hidden="true" />
                )}
                Confirmar
              </button>
            </div>
          </li>
        ))}
      </ul>

      {invitations.length > 0 && (
        <div className="border-t border-amber-100">
          <div className="px-4 pt-3">
            <h3 className="text-sm font-bold text-gray-900">Invitaciones enviadas · {invitations.length}</h3>
            <p className="text-xs text-gray-500">
              El deportista debe aceptar la invitación desde su cuenta. No puedes confirmarla por él.
            </p>
          </div>
          <ul className="max-h-[320px] divide-y divide-gray-100 overflow-y-auto">
            {invitations.map((row) => (
              <li key={row.id} className="flex items-center gap-3 px-4 py-3">
                <Avatar src={row.avatar_url} name={row.full_name} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-900">{row.full_name}</p>
                  <p className="truncate text-xs text-gray-500">
                    Invitado por la academia · esperando que el atleta acepte
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => decide.mutate({ id: row.id, decision: "remove" })}
                  disabled={Boolean(bulk) || busyId === row.id}
                  className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 px-3 text-xs font-semibold text-gray-600 hover:bg-gray-50 hover:text-red-600 disabled:opacity-60"
                >
                  {busyId === row.id ? (
                    <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                  ) : (
                    "Cancelar invitación"
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

/**
 * /scout/my-athletes — "Mis deportistas": the athletes this coach is actually
 * linked to (confirmed academy roster + approved contact requests), each with
 * its profile and report, plus the academy's pending join requests.
 */
const MyAthletesPage = () => {
  const rosterQuery = useAcademyRosterMembers();
  const myAthletesQuery = useMyAthletes();
  const decide = useDecideRosterMember();
  const [search, setSearch] = useState("");

  const academy = rosterQuery.data?.academy ?? null;
  const rosterItems = useMemo(() => rosterQuery.data?.items ?? [], [rosterQuery.data]);
  const pending = useMemo(() => rosterItems.filter((r) => r.status === "pending"), [rosterItems]);
  const rosterRowByAthlete = useMemo(
    () => new Map(rosterItems.filter((r) => r.status === "confirmed").map((r) => [String(r.athlete_id), r])),
    [rosterItems],
  );

  const athletes = useMemo(() => myAthletesQuery.data ?? [], [myAthletesQuery.data]);
  const visible = useMemo(() => {
    const q = normalize(search.trim());
    return q ? athletes.filter((a) => normalize(a.full_name).includes(q)) : athletes;
  }, [athletes, search]);
  const academyCount = athletes.filter((a) => a.in_academy).length;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="mx-auto w-full max-w-5xl space-y-6 p-4 sm:p-6 lg:p-8">
        <header className="space-y-1">
          <h1 className="text-2xl font-bold text-gray-900">Mis deportistas</h1>
          <p className="text-sm text-gray-500">
            {academy?.verified
              ? `Tu plantel en ${academy.name} y los deportistas que aceptaron tu solicitud de contacto.`
              : "Los deportistas que aceptaron tu solicitud de contacto."}
          </p>
        </header>

        {academy && !academy.verified && (
          <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 shadow-sm">
            <p className="font-semibold">
              Tu membresía en {academy.name} todavía no está verificada.
            </p>
            <p className="mt-1">
              Athletain debe verificar la academia antes de que funcione el plantel: hasta entonces no verás a sus
              deportistas ni podrás confirmar solicitudes o enviar invitaciones. Si la academia ya tiene un responsable
              verificado, él también puede verificarte.
            </p>
          </div>
        )}

        {academy?.verified && pending.length > 0 && <PendingRequests academy={academy} pending={pending} />}

        <section className="rounded-xl border border-gray-100 bg-white shadow-sm" aria-labelledby="linked-heading">
          <div className="flex flex-col gap-3 border-b border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 id="linked-heading" className="text-base font-bold text-gray-900">
                Vinculados · {athletes.length}
              </h2>
              {athletes.length > 0 && (
                <p className="text-xs text-gray-500">
                  {academyCount} de tu academia · {athletes.length - academyCount} por solicitud de contacto
                </p>
              )}
            </div>
            {athletes.length > 6 && (
              <label className="relative block sm:w-64">
                <span className="sr-only">Buscar por nombre</span>
                <Search
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nombre"
                  className="min-h-10 w-full rounded-lg border border-gray-300 pl-9 pr-3 text-sm focus:border-blue-900 focus:outline-none"
                />
              </label>
            )}
          </div>

          {myAthletesQuery.isLoading ? (
            <div className="flex items-center justify-center gap-2 p-10 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              Cargando deportistas…
            </div>
          ) : myAthletesQuery.isError ? (
            <p role="alert" className="p-6 text-sm text-red-600">
              No se pudieron cargar tus deportistas. {myAthletesQuery.error.message}
            </p>
          ) : athletes.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
              <p className="max-w-md text-sm text-gray-600">
                Aún no tienes deportistas vinculados. Aparecen aquí cuando un deportista acepta tu solicitud de contacto
                {academy?.verified ? " o cuando confirmas su ingreso a tu academia" : ""}.
              </p>
              <Link
                to="/scout/athletes"
                className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
              >
                Ir a la lista de atletas
              </Link>
            </div>
          ) : visible.length === 0 ? (
            <p className="p-6 text-center text-sm text-gray-500">Ningún deportista coincide con “{search}”.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {visible.map((athlete) => {
                const rosterRow = rosterRowByAthlete.get(String(athlete.id));
                const removing = decide.isPending && decide.variables?.id === rosterRow?.id;
                return (
                  <li key={athlete.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <Avatar src={athlete.avatar_url} name={athlete.full_name} />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">{athlete.full_name}</p>
                        <p className="truncate text-xs text-gray-500">{athleteLine(athlete) || athlete.sport}</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {athlete.in_academy && (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-green-700">
                              Academia
                            </span>
                          )}
                          {athlete.has_contact && (
                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700">
                              Contacto
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                      <Link
                        to={`/scout/athletes/${athlete.id}`}
                        className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-300 px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        <User size={14} aria-hidden="true" />
                        Perfil
                      </Link>
                      <Link
                        to={`/scout/reportes/${athlete.id}`}
                        className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-900 px-3 text-xs font-semibold text-white hover:bg-blue-800"
                      >
                        <FileText size={14} aria-hidden="true" />
                        Reporte
                      </Link>
                      {athlete.contact_request_id && (
                        <Link
                          to={`/scout/messages/${athlete.contact_request_id}`}
                          aria-label={`Mensajes con ${athlete.full_name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                        >
                          <MessageSquare size={14} aria-hidden="true" />
                        </Link>
                      )}
                      {rosterRow && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`¿Quitar a ${athlete.full_name} de ${academy?.name || "tu academia"}?`)) {
                              decide.mutate({ id: rosterRow.id, decision: "remove" });
                            }
                          }}
                          disabled={removing}
                          className="px-2 text-xs font-semibold text-gray-400 hover:text-red-600 disabled:opacity-60"
                        >
                          {removing ? "Quitando…" : "Quitar de la academia"}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default MyAthletesPage;
